import { z } from "zod";
import { entityId, nullableEntityId } from "./entityId";

const finiteNumber = z.number().finite();
const nonNegativeNumber = finiteNumber.min(0);
const nonNegativeInteger = finiteNumber.int().min(0);
const percentage = finiteNumber.min(-100).max(100);
const nonNegativePercentage = finiteNumber.min(0).max(100);
const validDate = z.string().min(1).refine((value) => Number.isFinite(Date.parse(value)), "Must be a valid date.");
const nullableNumber = finiteNumber.nullable();
const nullableSignedInteger = finiteNumber.int().nullable();
const nullableNonNegativeNumber = nonNegativeNumber.nullable();
const nullableNonNegativeInteger = nonNegativeInteger.nullable();
const nullablePercentage = percentage.nullable();
const nullableNonNegativePercentage = nonNegativePercentage.nullable();
const dailySalesUnknownReasonSchema = z.enum([
  "missing_attribution",
  "dangling_supplier_reference",
  "archived_placeholder",
]);

const provenanceSchema = z.object({
  kind: z.enum([
    "authoritative_backend_aggregate",
    "observed_row_value",
    "frontend_display_derivation",
    "modeled_estimated",
    "unknown",
  ]),
  authority: z.enum(["authoritative", "observed", "derived", "modeled", "unknown"]),
  actionability: z.enum(["actionable", "informational", "blocked", "unknown"]),
  unit: z.string().nullable().optional(),
  denominator: z.string().nullable().optional(),
  contextFingerprint: z.string().regex(/^sha256:[0-9a-f]{64}$/).nullable().optional(),
  formulaVersion: z.string().nullable().optional(),
  coverage: z.enum(["complete", "partial", "empty", "unavailable"]).nullable().optional(),
  limitation: z.string().nullable().optional(),
}).passthrough();

const analyticsContextDescriptorSchema = z.object({
  contractVersion: z.string().min(1),
  state: z.string().min(1),
  fingerprint: z.string().regex(/^sha256:[0-9a-f]{64}$/).nullable().optional(),
  unavailableReason: z.string().nullable().optional(),
  dateBoundaryConvention: z.string().nullable().optional(),
  requestedPeriodFromUtc: validDate.nullable().optional(),
  requestedPeriodToUtc: validDate.nullable().optional(),
  effectivePeriodFromUtc: validDate.nullable().optional(),
  effectivePeriodToUtc: validDate.nullable().optional(),
  observedPeriodFromUtc: validDate.nullable().optional(),
  observedPeriodToUtc: validDate.nullable().optional(),
  requestedDataScope: z.string().nullable().optional(),
  effectiveDataScope: z.string().nullable().optional(),
  notAppliedDimensions: z.array(z.string()).optional(),
  suppressedSources: z.array(z.string()).optional(),
  dataScopeSource: z.string().nullable().optional(),
  populationKey: z.string().nullable().optional(),
  populationFilters: z.record(z.string(), z.string().nullable()).optional(),
  sourceDataset: z.string().nullable().optional(),
  sourceGeneration: z.string().nullable().optional(),
  formulaVersion: z.string().nullable().optional(),
  materializerGeneration: z.string().nullable().optional(),
  cacheGeneration: z.string().nullable().optional(),
  rowLimitSemantics: z.string().nullable().optional(),
}).passthrough();

const decisionReadinessSchema = z.object({
  state: z.enum(["decision_ready", "signal_only", "blocked", "unavailable"]),
  surfaceRole: z.string().min(1),
  recommendationAllowed: z.boolean().nullable().optional(),
  reasonCodes: z.array(z.string()),
  evidenceReferences: z.array(z.string()),
  repairPath: z.string().nullable().optional(),
});

export const analyticsResponseMetaSchema = z.object({
  success: z.boolean(),
  readinessId: z.string().nullable().optional(),
  recoveryInstruction: z.string().nullable().optional(),
  contractDiagnostic: z.object({
    relation: z.string(),
    missingPart: z.string(),
    schema: z.string().nullable().optional(),
    missingColumn: z.string().nullable().optional(),
    foundInSchemas: z.array(z.string()).nullable().optional(),
  }).nullable().optional(),
  generatedAtUtc: validDate.optional(),
  lastRefreshAtUtc: validDate.nullable().optional(),
  requestedPeriodFromUtc: validDate.nullable().optional(),
  requestedPeriodToUtc: validDate.nullable().optional(),
  effectivePeriodFromUtc: validDate.nullable().optional(),
  effectivePeriodToUtc: validDate.nullable().optional(),
  requestedDataScope: z.string().nullable().optional(),
  effectiveDataScope: z.string().nullable().optional(),
  dataScopeSource: z.string().nullable().optional(),
  observedPeriodFromUtc: validDate.nullable().optional(),
  observedPeriodToUtc: validDate.nullable().optional(),
  defaultPeriodBasis: z.string().nullable().optional(),
  comparisonUnavailableReasonCode: z.string().nullable().optional(),
  dataQualityStatus: z.string().nullable().optional(),
  recommendationAllowed: z.boolean().nullable().optional(),
  operationsIntegrityStatus: z.string().nullable().optional(),
  operationsIntegrityCheckedAtUtc: validDate.nullable().optional(),
  operationsIntegrityEvidenceId: z.string().nullable().optional(),
  operationsIntegrityFamily: z.string().nullable().optional(),
  operationsIntegrityContextFingerprint: z.string().nullable().optional(),
  operationsIntegritySourceGeneration: z.string().nullable().optional(),
  operationsIntegrityContextMatches: z.boolean().nullable().optional(),
  isPartial: z.boolean().optional(),
  metricProvenance: z.record(z.string(), provenanceSchema).nullable().optional(),
  context: analyticsContextDescriptorSchema.nullable().optional(),
  decisionReadiness: decisionReadinessSchema.nullable().optional(),
}).passthrough();

const optionalMeta = analyticsResponseMetaSchema.nullable().optional();

const recommendationSchema = z.object({
  confidencePct: nullableNonNegativePercentage,
  reliabilityPct: nullableNonNegativePercentage,
  dataQualityStatus: z.string(),
  reasonCodes: z.array(z.string()),
}).passthrough();

const colorRecommendationSchema = z.object({
  status: z.enum(["increase_focus", "maintain", "review", "do_not_trust", "insufficient_data"]),
  label: z.string().trim().min(1),
  summary: z.string().trim().min(1),
  confidencePct: nullableNonNegativePercentage,
  reliabilityPct: nullableNonNegativePercentage,
  dataQualityStatus: z.string().trim().min(1),
  recommendationAllowed: z.boolean(),
  reasonCodes: z.array(z.string().trim().min(1)),
}).passthrough();

const salesStatSchema = {
  preNivelacijePromet: finiteNumber,
  preNivelacijeKolicina: nonNegativeNumber,
  posleNivelacijePromet: finiteNumber,
  posleNivelacijeKolicina: nonNegativeNumber,
  ukupanPromet: finiteNumber,
  ukupnaKolicina: nonNegativeNumber,
  previousPeriodRevenue: nullableNumber,
  previousPeriodUnits: nullableNumber,
  brojArtikalaSaNivelacijom: nonNegativeInteger,
  brojArtikalaUkupno: nonNegativeInteger,
  revenueWithCost: finiteNumber,
  estimatedCostRevenue: finiteNumber,
  marginContribution: finiteNumber,
  marginDataCoveragePct: nullableNonNegativePercentage,
  fallbackCostCoveragePct: nullableNonNegativePercentage,
  marginPct: percentage,
  revenueWithNivelacijaSplit: finiteNumber,
  popRevenueChangePct: nullableNumber,
  popUnitsChangePct: nullableNumber,
  prePostNivelacijaRevenueImpactPct: nullableNumber,
  prePostNivelacijaUnitsImpactPct: nullableNumber,
  prePostNivelacijaRevenueCoveragePct: nullableNonNegativePercentage,
  sharePct: nullableNumber.optional(),
  sharePctBasis: z.literal("net_sales_signed").nullable().optional(),
  sharePctNumerator: nullableNumber.optional(),
  sharePctDenominator: nullableNumber.optional(),
  sharePctUnavailableReason: z.literal("non_positive_net_sales_denominator").nullable().optional(),
  reliabilityPct: nullableNonNegativePercentage.optional(),
  recommendation: recommendationSchema.optional(),
};

const colorSalesStatSchema = {
  preNivelacijePromet: finiteNumber,
  preNivelacijeKolicina: finiteNumber,
  posleNivelacijePromet: finiteNumber,
  posleNivelacijeKolicina: finiteNumber,
  ukupanPromet: finiteNumber,
  ukupnaKolicina: finiteNumber,
  previousPeriodRevenue: nullableNumber,
  previousPeriodUnits: nullableNumber,
  brojArtikalaSaNivelacijom: nonNegativeInteger,
  brojArtikalaUkupno: nonNegativeInteger,
  revenueWithCost: finiteNumber,
  estimatedCostRevenue: finiteNumber,
  marginContribution: finiteNumber,
  marginDataCoveragePct: nullableNonNegativePercentage,
  fallbackCostCoveragePct: nullableNonNegativePercentage,
  marginPct: nullablePercentage,
  revenueWithNivelacijaSplit: finiteNumber,
  comparablePreRevenue: finiteNumber,
  comparablePostRevenue: finiteNumber,
  comparablePreQuantity: finiteNumber,
  comparablePostQuantity: finiteNumber,
  popRevenueChangePct: nullableNumber,
  popUnitsChangePct: nullableNumber,
  prePostNivelacijaRevenueImpactPct: nullableNumber,
  prePostNivelacijaUnitsImpactPct: nullableNumber,
  prePostNivelacijaRevenueCoveragePct: nullableNonNegativePercentage,
  prePostSignalNote: z.string().nullable(),
  prePostComparableArticleCount: nonNegativeInteger,
  sharePct: nullableNumber.optional(),
  sharePctBasis: z.literal("net_sales_signed").nullable().optional(),
  sharePctNumerator: nullableNumber.optional(),
  sharePctDenominator: nullableNumber.optional(),
  sharePctUnavailableReason: z.literal("non_positive_net_sales_denominator").nullable().optional(),
  decisionScore: nullableNonNegativePercentage.optional(),
  reliabilityPct: nullableNonNegativePercentage.optional(),
  recommendation: colorRecommendationSchema,
  promenaPrometa: nullableNumber.optional(),
};

const costTotalsSchema = {
  ukupanPromet: nonNegativeNumber,
  ukupanMarzniDoprinos: finiteNumber,
  prePromet: nonNegativeNumber,
  poslePromet: nonNegativeNumber,
  ukupnaKolicina: nonNegativeNumber,
  preKolicina: nonNegativeNumber,
  posleKolicina: nonNegativeNumber,
  previousPeriodRevenue: nullableNumber,
  previousPeriodUnits: nullableNumber,
  popRevenueChangePct: nullableNumber,
  popUnitsChangePct: nullableNumber,
  prePostNivelacijaRevenueImpactPct: nullableNumber,
  prePostNivelacijaUnitsImpactPct: nullableNumber,
  promenaPrometaPct: nullableNumber.optional(),
};

const colorCostTotalsSchema = {
  ...costTotalsSchema,
  ukupanPromet: finiteNumber,
  prePromet: finiteNumber,
  poslePromet: finiteNumber,
  ukupnaKolicina: finiteNumber,
  preKolicina: finiteNumber,
  posleKolicina: finiteNumber,
  comparablePreRevenue: finiteNumber,
  comparablePostRevenue: finiteNumber,
  comparablePreQuantity: finiteNumber,
  comparablePostQuantity: finiteNumber,
  comparableArticleCount: nonNegativeInteger,
  comparableRevenueCoveragePct: nullableNonNegativePercentage,
  prePostSignalNote: z.string().nullable(),
  observedPreRevenue: finiteNumber,
  observedPostRevenue: finiteNumber,
  observedPreQuantity: finiteNumber,
  observedPostQuantity: finiteNumber,
  weightedKnownMarginPct: nullableNumber,
  weightedKnownMarginRevenue: finiteNumber,
  decisionScore: nullableNonNegativePercentage.optional(),
  recommendationSummary: z.object({
    increaseFocus: nonNegativeInteger,
    maintain: nonNegativeInteger,
    review: nonNegativeInteger,
    doNotTrust: nonNegativeInteger,
    insufficientData: nonNegativeInteger,
  }),
};

const colorDataQualitySchema = z.object({
  missingCostRevenue: finiteNumber,
  missingCostRevenueSharePct: nullableNonNegativePercentage,
  estimatedCostRevenue: finiteNumber.optional(),
  estimatedCostRevenueSharePct: nullableNonNegativePercentage.optional(),
  unknownColorRevenue: finiteNumber,
  unknownColorRevenueSharePct: nullableNonNegativePercentage,
  revenueWithNivelacijaSplit: finiteNumber,
  revenueWithNivelacijaSplitSharePct: nullableNonNegativePercentage,
  observedRevenueWithNivelacijaSplit: finiteNumber,
  observedRevenueWithNivelacijaSplitSharePct: nullableNonNegativePercentage,
  signedRevenuePolicy: z.string().optional(),
  signedQuantityPolicy: z.string().optional(),
  costQualityDenominatorStatus: z.string().optional(),
  weightedKnownMarginPct: nullableNumber,
  weightedKnownMarginRevenue: finiteNumber,
}).passthrough();

const shoeTypeRecommendationSchema = z.object({
  status: z.enum(["increase_focus", "maintain", "review", "do_not_trust", "insufficient_data"]),
  label: z.string().trim().min(1),
  summary: z.string().trim().min(1),
  confidencePct: nullableNonNegativePercentage,
  reliabilityPct: nullableNonNegativePercentage,
  dataQualityStatus: z.enum(["good", "warning", "critical", "insufficient_data"]),
  recommendationAllowed: z.boolean(),
  reasonCodes: z.array(z.string().trim().min(1)),
});

const shoeDataQualitySchema = z.object({
  costCoveredRevenue: nonNegativeNumber,
  costCoveredRevenueSharePct: nullableNonNegativePercentage,
  missingCostRevenue: nonNegativeNumber,
  missingCostRevenueSharePct: nullableNonNegativePercentage,
  noCostRevenue: nonNegativeNumber,
  noCostRevenueSharePct: nullableNonNegativePercentage,
  historicalCostRevenue: nonNegativeNumber,
  historicalCostRevenueSharePct: nullableNonNegativePercentage,
  snapshotCostRevenue: nonNegativeNumber,
  snapshotCostRevenueSharePct: nullableNonNegativePercentage,
  estimatedCostRevenue: nonNegativeNumber.optional(),
  estimatedCostRevenueSharePct: nullableNonNegativePercentage.optional(),
  revenueWithNivelacijaSplit: nonNegativeNumber,
  revenueWithNivelacijaSplitSharePct: nullableNonNegativePercentage,
  unknownTypeRevenue: nonNegativeNumber,
  unknownTypeRevenueSharePct: nullableNonNegativePercentage,
});

const seasonSchema = z.object({
  id: entityId,
  naziv: z.string(),
  datumOd: validDate,
  datumDo: validDate,
}).passthrough();

export const colorSalesStatsResponseSchema = z.object({
  generatedAt: validDate,
  meta: analyticsResponseMetaSchema,
  fromDate: validDate.nullable(),
  toDate: validDate.nullable(),
  dataWindowFrom: validDate.nullable(),
  dataWindowTo: validDate.nullable(),
  sezonaId: nullableEntityId,
  storeId: nullableEntityId,
  dataScope: z.enum(["all", "existing", "imported"]),
  lineage: z.object({
    storeId: nullableEntityId,
    dataScope: z.string(),
    sourceFamily: z.string().trim().min(1).optional(),
    sourceLabel: z.string().trim().min(1).optional(),
    sourceTables: z.string().trim().min(1).optional(),
    observedPopulation: z.string().trim().min(1).optional(),
    costPolicy: z.string().trim().min(1).optional(),
    prePostPolicy: z.string().trim().min(1).optional(),
    unknownPolicy: z.string().trim().min(1).optional(),
    eventCount: nonNegativeInteger,
    eventArticleCount: nonNegativeInteger,
    salesArticleCount: nonNegativeInteger,
    salesArticlesWithMatchingNivelacija: nonNegativeInteger,
    storePolicy: z.string(),
    originPolicy: z.string(),
  }),
  colors: z.array(z.object({
    boja: z.string(),
    ...colorSalesStatSchema,
  }).passthrough()),
  totals: z.object(colorCostTotalsSchema).passthrough(),
  dataQuality: colorDataQualitySchema,
  sezone: z.array(seasonSchema),
}).passthrough();

export const shoeTypeSalesStatsResponseSchema = z.object({
  generatedAt: validDate,
  meta: optionalMeta,
  fromDate: validDate.nullable(),
  toDate: validDate.nullable(),
  dataWindowFrom: validDate.nullable(),
  dataWindowTo: validDate.nullable(),
  sezonaId: nullableEntityId,
  storeId: nullableEntityId,
  dataScope: z.string().nullable().optional(),
  shoeTypes: z.array(z.object({
    tipObuceId: nullableEntityId,
    tipObuceNaziv: z.string(),
    isUnknown: z.boolean(),
    ...salesStatSchema,
    costCoveredRevenue: nonNegativeNumber,
    costCoveredRevenueSharePct: nullableNonNegativePercentage,
    marginPct: nullablePercentage,
    totalCost: finiteNumber,
    historicalCostRevenue: nonNegativeNumber,
    historicalCostCoveragePct: nullableNonNegativePercentage,
    estimatedCostCoveragePct: nullableNonNegativePercentage,
    snapshotCostRevenue: nonNegativeNumber,
    snapshotCostCoveragePct: nullableNonNegativePercentage,
    noCostRevenue: nonNegativeNumber,
    noCostCoveragePct: nullableNonNegativePercentage,
    isEstimatedMargin: z.boolean(),
    marginQualityLabel: z.string().nullable(),
    // Older cached/API payloads may omit row-level margin presentation fields.
    // Recommendation and response-level data quality remain authoritative.
    marginQualityTier: z.string().nullable().optional(),
    marginQualityShortLabel: z.string().nullable().optional(),
    marginQualityTooltip: z.string().nullable().optional(),
    recommendation: shoeTypeRecommendationSchema,
    comparableRevenueWithNivelacijaSplit: nonNegativeNumber,
    comparablePreRevenue: nonNegativeNumber,
    comparablePostRevenue: nonNegativeNumber,
    comparablePreQuantity: nonNegativeInteger,
    comparablePostQuantity: nonNegativeInteger,
    prePostSignalNote: z.string().nullable(),
    prePostComparableArticleCount: nonNegativeInteger,
    promenaPrometa: nullableNumber.optional(),
    promenaKolicine: nullableNumber,
  })),
  totals: z.object({
    ...costTotalsSchema,
    brojTipovaObuce: nonNegativeInteger,
    ukupanTrosak: finiteNumber,
    prosecnaMarza: nullablePercentage,
    weightedMarginRevenue: nullableNonNegativeNumber,
    historicalCostCoveragePct: nullableNonNegativePercentage,
    estimatedCostCoveragePct: nullableNonNegativePercentage,
    noCostCoveragePct: nullableNonNegativePercentage,
    snapshotCostRevenue: nonNegativeNumber,
    snapshotCostCoveragePct: nullableNonNegativePercentage,
    isSnapshotActive: z.boolean(),
    snapshotGeneratedAtUtc: validDate.nullable(),
    isEstimatedMargin: z.boolean(),
    marginQualityLabel: z.string().nullable(),
    marginQualityTier: z.string().nullable(),
    marginQualityShortLabel: z.string().nullable(),
    marginQualityTooltip: z.string().nullable(),
    comparablePreRevenue: nonNegativeNumber,
    comparablePostRevenue: nonNegativeNumber,
    comparablePreQuantity: nonNegativeInteger,
    comparablePostQuantity: nonNegativeInteger,
    comparableArticleCount: nonNegativeInteger,
    comparableRevenueCoveragePct: nullableNonNegativePercentage,
    observedPreRevenue: nonNegativeNumber,
    observedPostRevenue: nonNegativeNumber,
    observedPreQuantity: nonNegativeInteger,
    observedPostQuantity: nonNegativeInteger,
  }),
  dataQuality: shoeDataQualitySchema,
  sezone: z.array(seasonSchema),
}).passthrough();

const supplierRecommendationSchema = z.object({
  status: z.enum(["increase_focus", "maintain", "review", "do_not_trust", "insufficient_data"]),
  label: z.string().trim().min(1),
  summary: z.string(),
  confidencePct: nullableNonNegativePercentage,
  reliabilityPct: nullableNonNegativePercentage,
  dataQualityStatus: z.enum(["good", "warning", "critical", "insufficient_data"]),
  recommendationAllowed: z.boolean(),
  reasonCodes: z.array(z.string().trim().min(1)),
}).passthrough();

const supplierFootwearBreakdownSchema = z.object({
  tipObuceId: nullableEntityId,
  tipObuceNaziv: z.string(),
  ukupanPromet: finiteNumber,
  ukupnaKolicina: finiteNumber,
  brojArtikala: nonNegativeInteger,
  totalCost: finiteNumber,
  marginContribution: finiteNumber,
  marginPct: finiteNumber,
  shareOfSupplierRevenuePct: nullableNumber,
  shareOfSupplierMarginContributionPct: nullableNumber,
  previousPeriodRevenue: nullableNumber,
  previousPeriodUnits: nullableSignedInteger,
  popRevenueChangePct: nullableNumber,
  popUnitsChangePct: nullableNumber,
  historicalCostRevenue: finiteNumber.optional(),
  historicalCostCoveragePct: nullableNumber.optional(),
  estimatedCostRevenue: finiteNumber.optional(),
  estimatedCostCoveragePct: nullableNumber.optional(),
  snapshotCostRevenue: finiteNumber.optional(),
  snapshotCostCoveragePct: nullableNumber.optional(),
  noCostRevenue: finiteNumber.optional(),
  noCostCoveragePct: nullableNumber.optional(),
  marginQualityLabel: z.string().nullable().optional(),
  marginQualityTier: z.string().nullable().optional(),
  marginQualityShortLabel: z.string().nullable().optional(),
  marginQualityTooltip: z.string().nullable().optional(),
}).passthrough();

const supplierSalesStatSchema = {
  dobavljacId: nullableEntityId,
  dobavljacNaziv: z.string(),
  isUnknown: z.boolean(),
  preNivelacijePromet: finiteNumber,
  preNivelacijeKolicina: finiteNumber,
  posleNivelacijePromet: finiteNumber,
  posleNivelacijeKolicina: finiteNumber,
  comparablePreNivelacijePromet: finiteNumber.optional(),
  comparablePostNivelacijePromet: finiteNumber.optional(),
  comparablePreNivelacijeKolicina: finiteNumber.optional(),
  comparablePostNivelacijeKolicina: finiteNumber.optional(),
  ukupanPromet: finiteNumber,
  ukupnaKolicina: finiteNumber,
  previousPeriodRevenue: nullableNumber,
  previousPeriodUnits: nullableSignedInteger,
  brojArtikalaSaNivelacijom: nonNegativeInteger,
  brojArtikalaUkupno: nonNegativeInteger,
  revenueWithCost: finiteNumber,
  estimatedCostRevenue: finiteNumber,
  marginContribution: finiteNumber,
  marginDataCoveragePct: nullableNumber,
  fallbackCostCoveragePct: nullableNumber,
  marginPct: finiteNumber,
  totalCost: finiteNumber,
  historicalCostRevenue: finiteNumber,
  historicalCostCoveragePct: nullableNumber,
  estimatedCostCoveragePct: nullableNumber,
  snapshotCostRevenue: finiteNumber,
  snapshotCostCoveragePct: nullableNumber,
  noCostRevenue: finiteNumber,
  noCostCoveragePct: nullableNumber,
  isEstimatedMargin: z.boolean(),
  marginQualityLabel: z.string().nullable(),
  marginQualityTier: z.string().nullable(),
  marginQualityShortLabel: z.string().nullable(),
  marginQualityTooltip: z.string().nullable(),
  popRevenueChangePct: nullableNumber,
  popUnitsChangePct: nullableNumber,
  prePostNivelacijaRevenueImpactPct: nullableNumber,
  prePostNivelacijaUnitsImpactPct: nullableNumber,
  prePostNivelacijaRevenueCoveragePct: nullableNumber,
  prePostSignalNote: z.string().nullable(),
  prePostComparableArticleCount: nonNegativeInteger,
  primaryFootwearType: z.string().nullable(),
  primaryFootwearTypeSharePct: nullableNumber,
  footwearTypeCount: nonNegativeInteger,
  footwearBreakdown: z.array(supplierFootwearBreakdownSchema),
  sharePct: nullableNumber,
  sharePctNumerator: nullableNumber.optional(),
  sharePctDenominator: nullableNumber.optional(),
  sharePctState: z.string().nullable().optional(),
  sharePctBasis: z.literal("positive_net_revenue").nullable().optional(),
  sharePctNumeratorBasis: z.literal("positive_supplier_net_revenue").nullable().optional(),
  sharePctDenominatorBasis: z.literal("positive_net_revenue_declared_population").nullable().optional(),
  sharePctIncludesUnknown: z.boolean().optional(),
  shareOfMarginContribution: nullableNumber,
  shareOfProfit: nullableNumber,
  shareOfUnits: nullableNumber,
  reliabilityPct: nullableNonNegativePercentage,
  recommendation: supplierRecommendationSchema,
  promenaPrometa: nullableNumber.optional(),
  promenaKolicine: nullableNumber,
};

const supplierDataQualitySchema = z.object({
  missingCostQty: finiteNumber,
  missingCostRevenue: finiteNumber,
  missingCostRevenueSharePct: nullableNonNegativePercentage,
  noCostRevenue: finiteNumber,
  noCostRevenueSharePct: nullableNonNegativePercentage,
  costCoveredRevenue: finiteNumber,
  costCoveredRevenueSharePct: nullableNonNegativePercentage,
  historicalCostRevenue: finiteNumber,
  historicalCostRevenueSharePct: nullableNonNegativePercentage,
  snapshotCostRevenue: finiteNumber,
  snapshotCostRevenueSharePct: nullableNonNegativePercentage,
  estimatedCostRevenue: finiteNumber,
  estimatedCostRevenueSharePct: nullableNonNegativePercentage,
  costSourceBasis: z.string().trim().min(1),
  unknownSupplierRevenue: finiteNumber,
  unknownSupplierRevenueSharePct: nullableNonNegativePercentage,
  unknownSupplierRevenueShareState: z.string().nullable().optional(),
  positiveNetRevenueDenominator: nullableNumber.optional(),
  positiveNetRevenueDenominatorState: z.string().nullable().optional(),
  revenueWithNivelacijaSplit: finiteNumber,
  revenueWithNivelacijaSplitSharePct: nullableNumber,
}).passthrough();

export const supplierSalesStatsResponseSchema = z.object({
  generatedAt: validDate,
  meta: analyticsResponseMetaSchema,
  fromDate: validDate.nullable(),
  toDate: validDate.nullable(),
  dataWindowFrom: validDate.nullable(),
  dataWindowTo: validDate.nullable(),
  sezonaId: nullableEntityId,
  storeId: nullableEntityId,
  dataScope: z.enum(["all", "existing", "imported"]),
  provenanceBasis: z.string().nullable().optional(),
  recommendationAllowed: z.boolean().nullable().optional(),
  recommendationReferenceCohort: z.object({
    scope: z.union([z.literal("all_response_suppliers"), z.literal("known_supplier_rows")]),
    supplierCount: nonNegativeInteger,
    includesUnknown: z.boolean(),
    sharePctBasis: z.string().optional(),
    sharePctDenominator: z.string().optional(),
    sharePctIncludesUnknown: z.boolean().optional(),
    basis: z.string().trim().min(1),
  }).nullable().optional(),
  suppliers: z.array(z.object(supplierSalesStatSchema).passthrough()),
  totals: z.object({
    ukupanPromet: finiteNumber,
    positiveNetRevenueDenominator: nullableNumber.optional(),
    positiveNetRevenueDenominatorState: z.string().nullable().optional(),
    ukupanMarzniDoprinos: finiteNumber,
    ukupanTrosak: finiteNumber.optional(),
    prosecnaMarza: nullableNumber,
    weightedMarginRevenue: finiteNumber,
    weightedMarginContribution: finiteNumber,
    marginBenchmarkBasis: z.string().trim().min(1),
    historicalCostCoveragePct: nullableNumber,
    estimatedCostCoveragePct: nullableNumber,
    noCostCoveragePct: nullableNumber,
    snapshotCostRevenue: finiteNumber,
    snapshotCostCoveragePct: nullableNumber,
    isSnapshotActive: z.boolean(),
    snapshotGeneratedAtUtc: validDate.nullable(),
    isEstimatedMargin: z.boolean(),
    marginQualityLabel: z.string().nullable(),
    marginQualityTier: z.string().nullable(),
    marginQualityShortLabel: z.string().nullable(),
    marginQualityTooltip: z.string().nullable(),
    prePromet: finiteNumber,
    poslePromet: finiteNumber,
    ukupnaKolicina: finiteNumber,
    preKolicina: finiteNumber,
    posleKolicina: finiteNumber,
    observedPrePromet: finiteNumber.optional(),
    observedPoslePromet: finiteNumber.optional(),
    observedPreKolicina: finiteNumber.optional(),
    observedPosleKolicina: finiteNumber.optional(),
    comparablePrePromet: finiteNumber.optional(),
    comparablePoslePromet: finiteNumber.optional(),
    comparablePreKolicina: finiteNumber.optional(),
    comparablePosleKolicina: finiteNumber.optional(),
    prePostComparableArticleCount: nonNegativeInteger.optional(),
    prePostNivelacijaRevenueCoveragePct: nullableNumber.optional(),
    prePostSignalNote: z.string().nullable().optional(),
    previousPeriodRevenue: nullableNumber,
    previousPeriodUnits: nullableSignedInteger,
    brojDobavljaca: nonNegativeInteger,
    brojDobavljacTipObuceKombinacija: nonNegativeInteger.optional(),
    popRevenueChangePct: nullableNumber,
    popUnitsChangePct: nullableNumber,
    prePostNivelacijaRevenueImpactPct: nullableNumber,
    prePostNivelacijaUnitsImpactPct: nullableNumber,
    recommendationSummary: z.object({
      increaseFocus: nonNegativeInteger,
      maintain: nonNegativeInteger,
      review: nonNegativeInteger,
      doNotTrust: nonNegativeInteger,
      insufficientData: nonNegativeInteger,
    }),
  }).passthrough(),
  dataQuality: supplierDataQualitySchema,
  sezone: z.array(seasonSchema),
}).passthrough();

const vendorSalesNivelacijaRecommendationSchema = z.object({
  status: z.enum(["effective", "neutral", "ineffective", "immature", "insufficient_data"]),
  label: z.string().trim().min(1),
  summary: z.string(),
  confidencePct: nullableNonNegativePercentage,
  reliabilityPct: nullableNonNegativePercentage,
  dataQualityStatus: z.string().trim().min(1),
  recommendationAllowed: z.boolean().nullable().optional(),
  reasonCodes: z.array(z.string().trim().min(1)),
}).passthrough();

const vendorSalesNivelacijaDriverMetricSummarySchema = z.object({
  mean: nullableNumber,
  median: nullableNumber,
  sampleCount: nonNegativeInteger,
  meanWeighting: z.enum(["unweighted", "post_revenue_weighted"]),
}).passthrough();

const vendorSalesNivelacijaDriverSummarySchema = z.object({
  momentumRevenue: vendorSalesNivelacijaDriverMetricSummarySchema,
  elasticity: vendorSalesNivelacijaDriverMetricSummarySchema,
  didRevenue: vendorSalesNivelacijaDriverMetricSummarySchema,
  lostSalesOOS: vendorSalesNivelacijaDriverMetricSummarySchema,
}).passthrough();

const vendorSalesNivelacijaVendorSchema = z.object({
  vendorId: nullableEntityId,
  vendorName: z.string(),
  preQty: finiteNumber,
  preRevenue: finiteNumber,
  postQty: finiteNumber,
  postRevenue: finiteNumber,
  changeQty: finiteNumber,
  changeRevenue: finiteNumber,
  changePercent: nullableNumber,
  hasRevenueBaseline: z.boolean().optional(),
  revenueBaselineReason: z.string().nullable().optional(),
  hasQtyBaseline: z.boolean().optional(),
  qtyBaselineReason: z.string().nullable().optional(),
  absoluteChangeRevenue: finiteNumber,
  changeSharePercent: finiteNumber,
  postRevenueSharePercent: finiteNumber,
  avgCoveragePre30: nullableNonNegativePercentage,
  avgCoveragePost30: nullableNonNegativePercentage,
  avgSalesActivityRatePre30Pct: nullableNonNegativePercentage.optional(),
  avgSalesActivityRatePost30Pct: nullableNonNegativePercentage.optional(),
  avgSalesActiveDaysPre30: nullableNonNegativeNumber.optional(),
  avgSalesActiveDaysPost30: nullableNonNegativeNumber.optional(),
  hasComparableSalesWindow: z.boolean().optional(),
  semanticChangePercentRevenue: nullableNumber.optional(),
  semanticChangePercentQty: nullableNumber.optional(),
  articleCount: nonNegativeInteger,
  activeArticlesCount: nonNegativeInteger,
  increasedPriceArticlesCount: nonNegativeInteger,
  decreasedPriceArticlesCount: nonNegativeInteger,
  reliabilityPct: nullableNonNegativePercentage,
  recommendation: vendorSalesNivelacijaRecommendationSchema.nullable().optional(),
  comparableArticleCount: nonNegativeInteger.optional(),
  primaryFootwearType: z.string().nullable().optional(),
  primaryFootwearTypeSharePercent: nullableNonNegativePercentage.optional(),
  primaryFootwearTypeAvgElasticity: nullableNumber.optional(),
  avgElasticity: nullableNumber.optional(),
  driverMetrics: vendorSalesNivelacijaDriverSummarySchema.nullable().optional(),
  typeInsightsAuthoritative: z.boolean().optional(),
}).passthrough();

const vendorSalesNivelacijaArticleSchema = z.object({
  eventDate: validDate,
  vendorId: nullableEntityId,
  vendorName: z.string(),
  sku: z.string(),
  articleName: z.string(),
  category: z.string(),
  oldPrice: nullableNumber,
  newPrice: nullableNumber,
  preQty: finiteNumber,
  preRevenue: finiteNumber,
  postQty: finiteNumber,
  postRevenue: finiteNumber,
  changeQty: finiteNumber,
  changeRevenue: finiteNumber,
  changePercent: nullableNumber,
  coveragePre30: nullableNonNegativePercentage,
  coveragePost30: nullableNonNegativePercentage,
  salesActivityRatePre30Pct: nullableNonNegativePercentage.optional(),
  salesActivityRatePost30Pct: nullableNonNegativePercentage.optional(),
  salesActiveDaysPre30: nullableNonNegativeInteger.optional(),
  salesActiveDaysPost30: nullableNonNegativeInteger.optional(),
  hasSalesWindow: z.boolean(),
  hasPreSalesEvidence: z.boolean().optional(),
  hasPostSalesEvidence: z.boolean().optional(),
  hasComparableSalesWindow: z.boolean().optional(),
  hasQtyBaseline: z.boolean().optional(),
  qtyBaselineReason: z.string().nullable().optional(),
  hasRevenueBaseline: z.boolean().optional(),
  revenueBaselineReason: z.string().nullable().optional(),
  semanticChangePercentRevenue: nullableNumber.optional(),
  semanticChangePercentQty: nullableNumber.optional(),
  priceChanged: z.boolean(),
  priceChangePercent: nullableNumber,
  rolling7dPreRevenue: nullableNumber.optional(),
  rolling7dPostRevenue: nullableNumber.optional(),
  momentumRevenue: nullableNumber.optional(),
  priceElasticity: nullableNumber.optional(),
  didRevenue: nullableNumber.optional(),
  didQty: nullableNumber.optional(),
  lostSalesOOS: nullableNumber.optional(),
  oosRate: nullableNonNegativePercentage.optional(),
  metricReason: z.string().nullable().optional(),
}).passthrough();

const vendorSalesNivelacijaTotalsSchema = z.object({
  preQty: finiteNumber,
  preRevenue: finiteNumber,
  postQty: finiteNumber,
  postRevenue: finiteNumber,
  changeQty: finiteNumber,
  changeRevenue: finiteNumber,
  changePercent: nullableNumber,
  hasRevenueBaseline: z.boolean().optional(),
  revenueBaselineReason: z.string().nullable().optional(),
  hasQtyBaseline: z.boolean().optional(),
  qtyBaselineReason: z.string().nullable().optional(),
  semanticChangePercentRevenue: nullableNumber.optional(),
  semanticChangePercentQty: nullableNumber.optional(),
  vendorsCount: nonNegativeInteger,
  articlesCount: nonNegativeInteger,
  activeArticlesCount: nonNegativeInteger,
  avgRevenuePerArticlePre: finiteNumber,
  avgRevenuePerArticlePost: finiteNumber,
  avgPriceChangePercent: nullableNumber,
  absoluteChangeRevenue: finiteNumber,
  avgCoveragePre30: nullableNonNegativePercentage,
  avgCoveragePost30: nullableNonNegativePercentage,
  avgSalesActivityRatePre30Pct: nullableNonNegativePercentage.optional(),
  avgSalesActivityRatePost30Pct: nullableNonNegativePercentage.optional(),
  avgSalesActiveDaysPre30: nullableNonNegativeNumber.optional(),
  avgSalesActiveDaysPost30: nullableNonNegativeNumber.optional(),
  hasComparableSalesWindow: z.boolean(),
  comparableRows: nonNegativeInteger,
  comparableArticlesCount: nonNegativeInteger,
  comparableVendorsCount: nonNegativeInteger,
}).passthrough();

const vendorSalesNivelacijaDataQualitySchema = z.object({
  rawRows: nullableNonNegativeInteger,
  deduplicatedRows: nullableNonNegativeInteger,
  duplicateRowsRemoved: nullableNonNegativeInteger,
  cohortRows: nullableNonNegativeInteger.optional(),
  cohortRowsExcluded: nullableNonNegativeInteger.optional(),
  returnedRows: nullableNonNegativeInteger.optional(),
  truncatedRows: nullableNonNegativeInteger.optional(),
  comparableRows: nullableNonNegativeInteger.optional(),
  comparableSharePercent: nullableNonNegativePercentage.optional(),
  isDetailTruncated: z.boolean().nullable().optional(),
  cohortPolicy: z.string().nullable().optional(),
  inactiveRows: nullableNonNegativeInteger,
  unchangedPriceRows: nullableNonNegativeInteger,
  analyzedRows: nullableNonNegativeInteger,
  analyzedSharePercent: nullableNonNegativePercentage,
  lowPostCoverageRows: nullableNonNegativeInteger,
  avgCoveragePre30: nullableNonNegativePercentage,
  avgCoveragePost30: nullableNonNegativePercentage,
  lowPostActivityRows: nullableNonNegativeInteger.optional(),
  avgSalesActivityRatePre30Pct: nullableNonNegativePercentage.optional(),
  avgSalesActivityRatePost30Pct: nullableNonNegativePercentage.optional(),
  avgSalesActiveDaysPre30: nullableNonNegativeNumber.optional(),
  avgSalesActiveDaysPost30: nullableNonNegativeNumber.optional(),
}).passthrough();

const vendorSalesNivelacijaCategorySchema = z.object({
  category: z.string(),
  articlesCount: nonNegativeInteger,
  vendorsCount: nonNegativeInteger,
  preQty: finiteNumber,
  preRevenue: finiteNumber,
  postQty: finiteNumber,
  postRevenue: finiteNumber,
  changeQty: finiteNumber,
  changeRevenue: finiteNumber,
  changePercent: nullableNumber,
  hasRevenueBaseline: z.boolean().optional(),
  revenueBaselineReason: z.string().nullable().optional(),
  hasQtyBaseline: z.boolean().optional(),
  qtyBaselineReason: z.string().nullable().optional(),
  semanticChangePercentRevenue: nullableNumber.optional(),
  semanticChangePercentQty: nullableNumber.optional(),
  hasComparableSalesWindow: z.boolean(),
  comparableArticleCount: nonNegativeInteger,
  postRevenueSharePercent: nullableNonNegativePercentage.optional(),
  avgElasticity: nullableNumber.optional(),
}).passthrough();

const vendorSalesNivelacijaPriceDirectionSchema = z.object({
  segment: z.string(),
  articlesCount: nonNegativeInteger,
  vendorsCount: nonNegativeInteger,
  avgPriceChangePercent: nullableNumber,
  changeRevenue: finiteNumber,
  changePercent: nullableNumber,
  hasRevenueBaseline: z.boolean().optional(),
  revenueBaselineReason: z.string().nullable().optional(),
  semanticChangePercentRevenue: nullableNumber.optional(),
  hasComparableSalesWindow: z.boolean(),
  comparableArticleCount: nonNegativeInteger,
}).passthrough();

const vendorSalesNivelacijaInsightSchema = z.object({
  title: z.string(),
  value: z.string(),
  details: z.string(),
  tone: z.string(),
}).passthrough();

export const vendorSalesNivelacijaResponseSchema = z.object({
  generatedAt: validDate,
  windowDays: nullableNonNegativeInteger,
  vendorId: nullableEntityId,
  eventDate: validDate.nullable(),
  from: validDate.nullable(),
  to: validDate.nullable(),
  category: z.string().nullable(),
  includeInactive: z.boolean(),
  storeId: nullableEntityId,
  dataScope: z.enum(["all", "existing", "imported"]),
  scopeApplied: z.boolean(),
  categories: z.array(z.string()),
  vendorStats: z.array(vendorSalesNivelacijaVendorSchema),
  articleStats: z.array(vendorSalesNivelacijaArticleSchema),
  totals: vendorSalesNivelacijaTotalsSchema,
  dataQuality: vendorSalesNivelacijaDataQualitySchema.nullable().optional(),
  categoryStats: z.array(vendorSalesNivelacijaCategorySchema),
  typeInsightsAuthoritative: z.boolean().optional(),
  typeInsightsSource: z.string().nullable().optional(),
  typeInsightsDenominator: z.string().nullable().optional(),
  priceDirectionStats: z.array(vendorSalesNivelacijaPriceDirectionSchema),
  insights: z.array(vendorSalesNivelacijaInsightSchema),
  avgMomentumRevenue: nullableNumber.optional(),
  avgElasticity: nullableNumber.optional(),
  avgDidRevenue: nullableNumber.optional(),
  avgLostSalesOOS: nullableNumber.optional(),
  driverMetrics: vendorSalesNivelacijaDriverSummarySchema.nullable().optional(),
  oosRate: nullableNonNegativePercentage.optional(),
  metricsStatus: z.string().nullable().optional(),
  recommendationAllowed: z.boolean().nullable().optional(),
  dataCoverageStatus: z.string().optional(),
  dataCoverageReason: z.string().optional(),
  meta: optionalMeta,
}).passthrough();

export const vendorSalesNivelacijaOptionsSchema = z.array(z.object({
  eventDate: validDate,
  eventsCount: nonNegativeInteger,
  vendorsCount: nullableNonNegativeInteger,
  articlesCount: nullableNonNegativeInteger,
  activeArticlesCount: nullableNonNegativeInteger,
  hasSalesWindow: z.boolean(),
  label: z.string(),
}).passthrough());

const dailySalesRowSchema = z.object({
  date: validDate,
  firstShiftTotalItems: nullableNumber,
  secondShiftTotalItems: nullableNumber,
  totalRevenue: nullableNumber,
  topSupplierCounts: z.array(nullableNumber),
  othersCount: nullableNumber,
  totalItemsSold: nullableNumber,
}).passthrough();

const dailySalesMetadataSchema = z.object({
  totalDays: nullableNonNegativeInteger,
  uniqueSuppliersInRange: nullableNonNegativeInteger,
  // Quantities and derived ratios use net signed sales evidence: returns and
  // corrections can legitimately make these values negative.
  unknownSupplierPct: nullableNumber,
  unknownSupplierItems: nullableSignedInteger,
  shiftAssignmentStatus: z.string().nullable().optional(),
  shiftTimeZone: z.string().nullable().optional(),
  shiftTimestampBasis: z.string().nullable().optional(),
  shiftTimestampBasisKnownRows: nullableNonNegativeInteger.optional(),
  shiftTimestampBasisUnknownRows: nullableNonNegativeInteger.optional(),
  shiftTimestampBasisUnknownRevenue: nullableNumber.optional(),
  offShiftItems: nullableSignedInteger,
  offShiftRevenue: nullableNumber,
  noTimeFallbackItems: nullableSignedInteger.optional(),
  noTimeFallbackRevenue: nullableNumber.optional(),
  totalItemsInRange: nullableSignedInteger,
  duplicateReceiptGroupCount: nullableNonNegativeInteger,
  duplicateReceiptHeaderCount: nullableNonNegativeInteger,
  receiptAmountMismatchCount: nullableNonNegativeInteger,
  receiptAmountMismatchRevenue: nullableNonNegativeNumber,
  receiptReconciliation: z.object({
    status: z.string(),
    reasonCode: z.string().nullable().optional(),
    matchedReceiptCount: nullableNonNegativeInteger,
    unmatchedReceiptCount: nullableNonNegativeInteger,
    unmatchedDnevnikReceiptCount: nullableNonNegativeInteger,
    mismatchCount: nullableNonNegativeInteger,
    mismatchAmount: nullableNonNegativeNumber,
  }).optional(),
  nonStandardReceiptCount: nullableNonNegativeInteger,
  nonStandardReceiptRevenue: nullableNumber,
  debtReceiptCount: nullableNonNegativeInteger,
  debtReceiptRevenue: nullableNumber,
  diagnosticsDataScope: z.string().nullable().optional(),
  availabilityDataScope: z.string().nullable().optional(),
  minAvailableDate: validDate.nullable(),
  maxAvailableDate: validDate.nullable(),
  warnings: z.array(z.string()).optional(),
}).passthrough();

export const dailySalesTableResponseSchema = z.object({
  requestedFrom: validDate,
  requestedTo: validDate,
  storeId: nullableEntityId,
  topN: nonNegativeInteger,
  dataScope: z.string(),
  supplierProjection: z.object({
    version: z.string(),
    rankBasis: z.string(),
    totalDistinctSupplierBuckets: nonNegativeInteger,
    returnedCount: nonNegativeInteger,
    coverage: z.string(),
    orderKeys: z.array(z.string()),
    hasUnknownAttribution: z.boolean(),
  }).optional(),
  topSuppliers: z.array(z.object({
    supplierBucketKey: z.string().optional(),
    supplierId: nullableEntityId,
    supplierName: z.string(),
    isUnknown: z.boolean(),
    unknownReason: dailySalesUnknownReasonSchema.nullable().optional(),
    attributionBasis: z.string().nullable().optional(),
    totalQty: nullableNumber,
    totalRevenue: nullableNumber,
  }).passthrough()),
  topSuppliersOrder: z.array(z.string()),
  dateRows: z.array(dailySalesRowSchema),
  metadata: dailySalesMetadataSchema,
  meta: optionalMeta,
}).passthrough();

const preNivelacijaScenarioSchema = z.object({
  expectedUnits30d: nonNegativeInteger,
  expectedRevenue30d: nonNegativeNumber,
  expectedMargin30d: finiteNumber,
  effectivePrice: nonNegativeNumber,
}).passthrough();

const preNivelacijaRecommendationStatusSchema = z.enum([
  "increase_focus",
  "maintain",
  "review",
  "do_not_trust",
  "insufficient_data",
]);

const preNivelacijaQualityStatusSchema = z.enum(["good", "warning", "critical", "insufficient_data"]);
const preNivelacijaReasonCodeSchema = z.string().trim().min(1);
const preNivelacijaPriorityBandSchema = z.enum(["high", "medium", "low"]);
const preNivelacijaSalesEvidenceStatusSchema = z.enum([
  "positive_net_sales",
  "no_sales_in_window",
  "zero_net_sales",
  "negative_net_sales",
  "signed_adjustment",
  "non_positive_net_with_returns",
]);
const preNivelacijaSalesEvidenceReasonSchema = z.enum([
  "no_sales_in_window",
  "zero_net_sales",
  "negative_net_sales",
  "signed_sales_adjustment",
  "signed_sales_non_positive",
]).nullable();

const preNivelacijaRecommendationSchema = z.object({
  status: preNivelacijaRecommendationStatusSchema,
  label: z.string().trim().min(1),
  summary: z.string().trim().min(1),
  confidencePct: nonNegativePercentage,
  reliabilityPct: nullableNonNegativePercentage,
  dataQualityStatus: preNivelacijaQualityStatusSchema,
  recommendationAllowed: z.boolean(),
  reasonCodes: z.array(preNivelacijaReasonCodeSchema),
}).passthrough();

const preNivelacijaShadowEvidenceSchema = z.object({
  version: z.string().trim().min(1),
  weeksOfCover: nullableNonNegativeNumber,
  weeksToSeasonEnd: nullableNumber,
  ageDays: nullableNonNegativeInteger,
  ageBasis: z.enum(["first_receipt", "first_positive_sale_fallback", "unavailable"]),
  sellThroughSinceReceipt: nullableNumber,
  sellThroughUnavailableReason: z.string().nullable(),
  coverGap: nullableNumber,
  score: nullableNonNegativePercentage,
  rank: nullableNonNegativeInteger,
  reasonCodes: z.array(preNivelacijaReasonCodeSchema),
}).passthrough();

const preNivelacijaShadowComparisonSchema = z.object({
  version: z.string().trim().min(1),
  methodology: z.string().trim().min(1),
  coverGapWeight: nonNegativeNumber,
  ageWeight: nonNegativeNumber,
  coverGapSaturationWeeks: nonNegativeNumber,
  ageSaturationDays: nonNegativeInteger,
  label: z.string().trim().min(1),
  rowsCompared: nonNegativeInteger,
  spearmanRankCorrelation: nullableNumber,
  topN: nonNegativeInteger,
  topNOverlapCount: nonNegativeInteger,
  rowsWithRankMovement: nonNegativeInteger,
  largestMovements: z.array(z.object({
    artikalId: entityId,
    storeId: nullableEntityId,
    sku: z.string(),
    canonicalRank: nonNegativeInteger,
    shadowRank: nonNegativeInteger,
    rankMovement: finiteNumber.int(),
    shadowScore: nonNegativePercentage,
    weeksOfCover: nullableNonNegativeNumber,
    weeksToSeasonEnd: nullableNumber,
    ageDays: nullableNonNegativeInteger,
    ageBasis: z.string(),
  }).passthrough()),
}).passthrough();

const preNivelacijaCandidateSchema = z.object({
  artikalId: entityId,
  sku: z.string().trim().min(1),
  storeId: nullableEntityId,
  storeName: z.string().trim().min(1),
  supplierId: nullableEntityId,
  seasonId: nullableEntityId,
  footwearTypeId: nullableEntityId,
  supplierName: z.string().trim().min(1),
  category: z.string().trim().min(1),
  footwearType: z.string().trim().min(1),
  season: z.string().trim().min(1),
  stockUnits: nonNegativeNumber,
  units180: finiteNumber,
  positiveUnits180: nonNegativeInteger,
  negativeUnits180: finiteNumber.int(),
  velocity180: finiteNumber,
  daysSinceLastSale: nullableNonNegativeInteger,
  salesHistoryStatus: z.enum(["never_sold", "no_sale_in_window", "sold"]).optional(),
  firstReceiptDateUtc: validDate.nullable().optional(),
  daysSinceReceipt: nullableNonNegativeInteger.optional(),
  canonicalRank: nonNegativeInteger.optional(),
  shadowV9: preNivelacijaShadowEvidenceSchema.optional(),
  receiptEvidenceStatus: z.enum(["received", "first_sale_fallback", "unknown"]).optional(),
  stockAgeStatus: z.enum(["new_stock", "established", "unknown"]).optional(),
  markdownEvents: nonNegativeInteger,
  avgMarkdownPct: nonNegativePercentage,
  grossMarginPctEst: nullableNumber,
  grossMarginPctSigned: nullableNumber.optional(),
  belowCost: z.boolean().nullable().optional(),
  seasonRecencyBoost: percentage,
  preNivelacijaScore: nonNegativePercentage,
  priorityBand: preNivelacijaPriorityBandSchema,
  scoreBreakdown: z.object({
    stockPressure: nonNegativePercentage,
    velocityRisk: nonNegativePercentage,
    recencyRisk: nonNegativePercentage,
    markdownOpportunity: nonNegativePercentage,
    marginPotential: nonNegativePercentage,
    seasonRecencyBoost: nonNegativePercentage,
  }).passthrough(),
  scenarioHighlightNow: preNivelacijaScenarioSchema,
  scenarioMarkdownNow: preNivelacijaScenarioSchema,
  marginDeltaHighlightVsMarkdown: finiteNumber,
  revenueDeltaHighlightVsMarkdown: finiteNumber,
  hasCompleteEvidence: z.boolean(),
  evidenceReason: z.string().nullable(),
  salesEvidenceStatus: preNivelacijaSalesEvidenceStatusSchema,
  salesEvidenceReason: preNivelacijaSalesEvidenceReasonSchema,
  confidence: z.string().trim().min(1),
  reliabilityPct: nonNegativePercentage,
  decisionScore: nonNegativePercentage,
  recommendationAllowed: z.boolean(),
  recommendation: preNivelacijaRecommendationSchema,
}).passthrough();

const preNivelacijaSupplierSchema = z.object({
  supplierId: nullableEntityId,
  supplierName: z.string().trim().min(1),
  highPrioritySkuCount: nonNegativeInteger,
  candidateSkuCount: nonNegativeInteger,
  stockUnitsAtRisk: nonNegativeInteger,
  estimatedAvoidableMarkdownLoss: finiteNumber,
  expectedHighlightRevenueUplift: finiteNumber,
  actionScore: finiteNumber,
  weekOverWeekRiskDeltaPct: nullableNumber,
  weekOverWeekEvidenceStatus: z.enum(["measured", "unavailable_non_positive_denominator"]),
}).passthrough();

const preNivelacijaQueueItemSchema = z.object({
  artikalId: entityId,
  sku: z.string().trim().min(1),
  storeId: nullableEntityId,
  storeName: z.string().trim().min(1),
  supplierName: z.string().trim().min(1),
  preNivelacijaScore: nonNegativePercentage,
  priorityBand: preNivelacijaPriorityBandSchema,
  owner: z.string().trim().min(1),
  status: z.string().trim().min(1),
  dueDateUtc: validDate,
  recommendationAllowed: z.boolean().optional(),
  recommendationStatus: z.string().optional(),
  recommendationLabel: z.string().optional(),
  recommendationSummary: z.string().optional(),
  confidencePct: finiteNumber.optional(),
  reliabilityPct: nullableNumber.optional(),
  dataQualityStatus: z.string().optional(),
  reasonCodes: z.array(z.string()).optional(),
}).passthrough();

const preNivelacijaNewStockQueueItemSchema = z.object({
  artikalId: entityId,
  sku: z.string().trim().min(1),
  storeId: nullableEntityId,
  supplierId: nullableEntityId,
  seasonId: nullableEntityId,
  footwearTypeId: nullableEntityId,
  storeName: z.string().trim().min(1),
  supplierName: z.string().trim().min(1),
  stockUnits: nonNegativeInteger,
  firstReceiptDateUtc: validDate,
  daysSinceReceipt: nonNegativeInteger,
  daysSinceLastSale: nullableNonNegativeInteger,
  salesHistoryStatus: z.enum(["never_sold", "no_sale_in_window", "sold"]),
  stockAgeStatus: z.literal("new_stock"),
  reasonCode: z.literal("new_stock"),
}).passthrough();

const preNivelacijaAlertSchema = z.object({
  type: z.string().trim().min(1),
  severity: z.enum(["critical", "warning", "info"]),
  message: z.string().trim().min(1),
  supplierName: z.string().trim().min(1).nullable(),
  artikalId: nullableEntityId,
}).passthrough();

const preNivelacijaModelEvidenceSchema = z.object({
  scoreBasis: z.string().trim().min(1),
  scoreReferencePopulation: z.string().trim().min(1),
  scoreNormalization: z.string().trim().min(1),
  scenarioBasis: z.string().trim().min(1),
  scenarioParameterVersion: z.string().trim().min(1),
  scenarioDisclaimer: z.string().trim().min(1),
}).passthrough();

const preNivelacijaFilterOptionSchema = z.object({
  id: entityId,
  label: z.string().trim().min(1),
  count: nonNegativeInteger.optional(),
}).passthrough();

export const preNivelacijaPriorityResponseSchema = z.object({
  generatedAtUtc: validDate,
  formulaVersion: z.string(),
  formulaDescription: z.string(),
  modelEvidence: preNivelacijaModelEvidenceSchema,
  summary: z.object({
    supplierCount: nonNegativeInteger,
    candidatesCount: nonNegativeInteger,
    highPriorityCount: nonNegativeInteger,
    increaseFocusCount: nonNegativeInteger,
    maintainCount: nonNegativeInteger,
    reviewCount: nonNegativeInteger,
    doNotTrustCount: nonNegativeInteger,
    insufficientDataCount: nonNegativeInteger,
    totalStockAtRisk: nullableNonNegativeInteger,
    totalStockAtRiskCoverageEligible: nonNegativeInteger,
    totalStockAtRiskCoverageTotal: nonNegativeInteger,
    estimatedAvoidableMarkdownLoss: nullableNumber,
    estimatedAvoidableMarkdownLossCoverageEligible: nonNegativeInteger,
    estimatedAvoidableMarkdownLossCoverageTotal: nonNegativeInteger,
    expectedHighlightRevenueUplift: nullableNumber,
    expectedHighlightRevenueUpliftCoverageEligible: nonNegativeInteger,
    expectedHighlightRevenueUpliftCoverageTotal: nonNegativeInteger,
    averagePreNivelacijaScore: finiteNumber,
  }).passthrough(),
  supplierLeaderboard: z.array(preNivelacijaSupplierSchema),
  filterFacets: z.object({
    suppliers: z.array(preNivelacijaFilterOptionSchema).optional(),
    seasons: z.array(preNivelacijaFilterOptionSchema),
    footwearTypes: z.array(preNivelacijaFilterOptionSchema),
    stores: z.array(preNivelacijaFilterOptionSchema).optional(),
  }).passthrough(),
  candidates: z.array(preNivelacijaCandidateSchema),
  queues: z.object({
    legacyCleanup: z.array(z.object({
      artikalId: entityId,
      sku: z.string(),
      storeId: nullableEntityId,
      storeName: z.string(),
      supplierId: nullableEntityId,
      supplierName: z.string(),
      seasonId: nullableEntityId,
      season: z.string(),
      footwearTypeId: nullableEntityId,
      footwearType: z.string(),
      stockUnits: nonNegativeInteger,
      reasonCodes: z.array(z.string()),
      recommendationAllowed: z.literal(false),
    }).passthrough()).optional(),
    legacyCleanupTotal: nonNegativeInteger.optional().nullable(),
    nonFootwearCleanup: z.array(z.object({
      artikalId: entityId,
      sku: z.string(),
      storeId: nullableEntityId,
      storeName: z.string(),
      supplierId: nullableEntityId,
      supplierName: z.string(),
      seasonId: nullableEntityId,
      season: z.string(),
      footwearTypeId: nullableEntityId,
      footwearType: z.string(),
      stockUnits: nonNegativeInteger,
      reasonCodes: z.array(z.string()),
      recommendationAllowed: z.literal(false),
    }).passthrough()).optional(),
    nonFootwearCleanupTotal: nonNegativeInteger.optional().nullable(),
    newStock: z.array(preNivelacijaNewStockQueueItemSchema).optional(),
    newStockTotal: nonNegativeInteger.optional().nullable(),
    highlightNow: z.array(preNivelacijaQueueItemSchema),
    highlightNowTotal: nonNegativeInteger.optional().nullable(),
    monitor: z.array(preNivelacijaQueueItemSchema),
    monitorTotal: nonNegativeInteger.optional().nullable(),
    likelyMarkdownSoon: z.array(preNivelacijaQueueItemSchema),
    likelyMarkdownSoonTotal: nonNegativeInteger.optional().nullable(),
    likelyMarkdownUnavailableReason: z.string().nullable().optional(),
  }).passthrough(),
  alerts: z.array(preNivelacijaAlertSchema),
  page: nonNegativeInteger,
  pageSize: nonNegativeInteger,
  totalCandidates: nonNegativeInteger,
  recommendationAllowed: z.boolean(),
  evidenceWindow: z.object({
    anchorBasis: z.string().optional(),
    anchorDateUtc: validDate.optional(),
    observedSourceHorizonUtc: validDate.nullable().optional(),
    decisionPopulationPolicy: z.string().optional(),
    salesWindowFromUtc: validDate,
    salesWindowToUtc: validDate,
    markdownWindowFromUtc: validDate,
    markdownWindowToUtc: validDate,
    timezone: z.string(),
    receiptPopulationPolicy: z.string(),
    salesQuantityPolicy: z.string(),
    signedReturnPolicy: z.string(),
    lastSaleRecencyPolicy: z.string(),
    nonPositiveNetPolicy: z.string(),
    previousWeekDenominatorPolicy: z.string(),
    candidatesWithReturns: nonNegativeInteger,
    candidatesWithNonPositiveNetSales: nonNegativeInteger,
    candidatesWithoutSalesInWindow: nonNegativeInteger,
    suppliersWithUnavailablePreviousWeekDenominator: nonNegativeInteger,
  }).nullable(),
  shadowV9Comparison: preNivelacijaShadowComparisonSchema.optional(),
  meta: optionalMeta,
}).passthrough();

const inventoryMetaFields = {
  meta: optionalMeta,
};

const inventoryListItemSchema = z.object({
  id: entityId,
  naziv: z.string(),
  kolicina: nullableNumber.optional(),
  minimalnaKolicina: nullableNumber.optional(),
  estimatedValue: nullableNumber.optional(),
  stockCoverDays: nullableNumber.optional(),
  sellThroughRatio: nullableNonNegativePercentage.optional(),
  signalConfidencePct: nullableNonNegativePercentage.optional(),
}).passthrough();

export const inventoryBalanceResponseSchema = z.object({
  totalSku: nonNegativeInteger,
  totalOnHand: nonNegativeNumber,
  lowStockCount: nonNegativeInteger,
  outOfStockCount: nonNegativeInteger,
  estimatedInventoryValue: nullableNumber.optional(),
  ...inventoryMetaFields,
}).passthrough();

export const inventoryPagedResponseSchema = z.object({
  items: z.array(inventoryListItemSchema),
  totalCount: nonNegativeInteger,
  pageNumber: nonNegativeInteger,
  pageSize: nonNegativeInteger,
  ...inventoryMetaFields,
}).passthrough();

const inventoryInsightItemSchema = z.object({
  id: entityId,
  storeId: nullableEntityId,
  supplierId: nullableEntityId,
  quantity: nonNegativeNumber,
  minimum: nonNegativeNumber,
  reorderGap: finiteNumber,
  estimatedValue: nullableNumber,
  unitCost: nullableNumber,
  costSource: z.string(),
  costMissing: z.boolean(),
  daysSinceMovement: nonNegativeNumber,
  sellThroughRatio: nullableNonNegativePercentage.optional(),
  signalConfidencePct: nonNegativePercentage,
}).passthrough();

export const inventoryInsightsResponseSchema = z.object({
  totalItems: nonNegativeInteger,
  totalEstimatedValue: nullableNonNegativeNumber,
  valueCoveragePct: nonNegativePercentage.optional(),
  unknownValueRows: nonNegativeInteger.optional(),
  aging: z.array(z.object({
    itemCount: nonNegativeInteger,
    totalUnits: nonNegativeNumber,
    estimatedValue: nullableNonNegativeNumber,
    valueCoveragePct: nonNegativePercentage.optional(),
    unknownValueRows: nonNegativeInteger.optional(),
  }).passthrough()),
  abc: z.array(z.object({
    itemCount: nonNegativeInteger,
    estimatedValue: nullableNonNegativeNumber,
    valueSharePct: nonNegativePercentage,
    valueCoveragePct: nonNegativePercentage.optional(),
    unknownValueRows: nonNegativeInteger.optional(),
  }).passthrough()),
  topAgedItems: z.array(inventoryInsightItemSchema),
  topCapitalLockedItems: z.array(inventoryInsightItemSchema),
  ...inventoryMetaFields,
}).passthrough();

export const inventoryDetailResponseSchema = z.object({
  id: entityId,
  estimatedValue: nonNegativeNumber,
  updatedAt: validDate,
  movementCount: nonNegativeInteger,
  daysSinceMovement: nonNegativeNumber,
  stockCoverDays: nullableNumber.optional(),
  sellThroughRatio: nullableNonNegativePercentage.optional(),
  signalConfidencePct: nonNegativePercentage,
  recommendationAllowed: z.boolean(),
  history: z.array(z.unknown()),
}).passthrough();

// Tier-1 composed surfaces share the same runtime meta/context/provenance
// contract. The page fields below deliberately validate only the stable
// business boundary; passthrough keeps additive endpoint fields compatible.
const tier1MetaEnvelope = { meta: analyticsResponseMetaSchema };

export const dashboardAdvancedResponseSchema = z.object({
  generatedAtUtc: validDate,
  cards: z.array(z.object({
    key: z.string(),
    value: finiteNumber,
    trendPct: nullableNumber.optional(),
    trendReasonCode: z.string().nullable().optional(),
    skuCount: nonNegativeInteger.nullable().optional(),
    reasonCode: z.string().nullable().optional(),
    lostSalesEstimateRsd: nullableNumber.optional(),
    lostSalesReasonCode: z.string().nullable().optional(),
    status: z.string(),
  }).passthrough()),
  insights: z.array(z.unknown()),
  actions: z.array(z.unknown()),
  validations: z.array(z.unknown()),
  ...tier1MetaEnvelope,
}).passthrough();

export const dashboardBootstrapResponseSchema = z.object({
  errors: z.array(z.string()),
  ...tier1MetaEnvelope,
}).passthrough();

// Coverage fields are optional so a frontend deployed ahead of the API (manual Render
// deploy) still renders; a missing coverage contract is shown as "coverage unavailable",
// never as 0 known / 0 unknown rows.
export const productDecisionCenterSummarySchema = z.object({
  lostSalesEstimate: nullableNumber,
  lostSalesEstimateKnownRows: nonNegativeInteger.optional(),
  lostSalesEstimateUnknownRows: nonNegativeInteger.optional(),
  lostSalesEstimateCoveragePct: nullableNonNegativePercentage.optional(),
  slowStockCapital: nullableNumber,
  slowStockCapitalKnownRows: nonNegativeInteger.optional(),
  slowStockCapitalUnknownRows: nonNegativeInteger.optional(),
  slowStockCapitalCoveragePct: nullableNonNegativePercentage.optional(),
}).passthrough();

export const productDecisionCenterResponseSchema = z.object({
  generatedAtUtc: validDate,
  periodFromUtc: validDate,
  periodToUtc: validDate,
  totalRows: nonNegativeInteger,
  summary: productDecisionCenterSummarySchema,
  rows: z.array(z.unknown()),
  ...tier1MetaEnvelope,
}).passthrough();

export const decisionBoardAggregateResponseSchema = z.object({
  generatedAtUtc: validDate,
  periodFromUtc: validDate.nullable().optional(),
  periodToUtc: validDate.nullable().optional(),
  warnings: z.array(z.string()),
  metrics: z.array(z.unknown()),
  sourceStates: z.array(z.unknown()),
  sections: z.array(z.unknown()),
  ...tier1MetaEnvelope,
}).passthrough();

export const dataQualityIssueListResponseSchema = z.object({
  page: nonNegativeInteger,
  pageSize: nonNegativeInteger,
  total: nonNegativeInteger,
  items: z.array(z.unknown()),
  ...tier1MetaEnvelope,
}).passthrough();

export const dataQualityHealthResponseSchema = z.object({
  generatedAt: validDate,
  lookbackDays: nonNegativeInteger,
  windowFrom: validDate,
  windowTo: validDate,
  observedPeriodFrom: validDate.nullable().optional(),
  observedPeriodTo: validDate.nullable().optional(),
  orphanArticleCount: nonNegativeInteger,
  totalArticleCount: nonNegativeInteger.optional(),
  missingSupplierArticleCount: nonNegativeInteger.optional(),
  missingCostArticleCount: nonNegativeInteger.optional(),
  missingCategoryArticleCount: nonNegativeInteger.optional(),
  totalRevenue: finiteNumber,
  hasRevenueEvidence: z.boolean(),
  missingCostRevenue: finiteNumber,
  missingCostRevenueSharePct: nullableNonNegativePercentage,
  unknownSupplierRevenue: finiteNumber,
  unknownSupplierRevenueSharePct: nullableNonNegativePercentage,
  score: nonNegativePercentage,
  scoreStatus: z.string(),
  scoreSummary: z.string(),
  thresholds: z.object({
    orphanArticleCount: nonNegativeInteger,
    missingCostRevenueSharePct: nonNegativePercentage,
    unknownSupplierRevenueSharePct: nonNegativePercentage,
  }).passthrough(),
  ...tier1MetaEnvelope,
}).passthrough();

export const dataQualityTopOffendersResponseSchema = z.object({
  issueType: z.string(),
  limit: nonNegativeInteger,
  count: nonNegativeInteger,
  items: z.array(z.unknown()),
  ...tier1MetaEnvelope,
}).passthrough();

export const dataQualityTrendResponseSchema = z.object({
  days: nonNegativeInteger,
  dataScope: z.string(),
  points: z.array(z.object({
    date: validDate,
    missingCostRevenueSharePct: nullableNonNegativePercentage,
    unknownSupplierRevenueSharePct: nullableNonNegativePercentage,
    orphanArticleCount: nonNegativeInteger,
  }).passthrough()),
  ...tier1MetaEnvelope,
}).passthrough();

export const pilotDataQualityIntakeResponseSchema = z.object({
  generatedAtUtc: validDate,
  dataScope: z.string(),
  readinessScore: nonNegativePercentage,
  readinessStatus: z.string(),
  loadedData: z.object({
    articlesCount: nonNegativeInteger,
    saleItemsCount: nonNegativeInteger,
    receiptsCount: nonNegativeInteger,
    suppliersCount: nonNegativeInteger,
    storesCount: nonNegativeInteger,
    firstSaleDate: validDate.nullable(),
    lastSaleDate: validDate.nullable(),
  }).passthrough(),
  issues: z.object({
    missingSupplierCount: nonNegativeInteger,
    missingCostCount: nonNegativeInteger,
    missingCategoryCount: nonNegativeInteger,
    saleWithoutArticleCount: nonNegativeInteger,
    zeroOrNegativePriceCount: nonNegativeInteger,
    missingSupplierNameCount: nonNegativeInteger,
  }).passthrough(),
  impact: z.object({
    revenueWithoutCostPercent: nullableNonNegativePercentage,
    articlesWithoutSupplierPercent: nullableNonNegativePercentage,
    recommendationsBlockedCount: nonNegativeInteger,
    ignoredRowsCount: nonNegativeInteger,
    insufficientSignalCount: nonNegativeInteger,
  }).passthrough(),
  recommendedActions: z.array(z.string()),
  ...tier1MetaEnvelope,
}).passthrough();

const durableReportPeriodSchema = z.object({
  fromUtc: validDate.nullable().optional(),
  toUtc: validDate.nullable().optional(),
  label: z.string(),
}).passthrough();

export const durableAnalyticsReportResponseSchema = z.object({
  reportId: z.string(),
  generatedAtUtc: validDate.nullable(),
  period: durableReportPeriodSchema,
  rows: z.array(z.unknown()),
  sections: z.array(z.unknown()),
  payload: z.record(z.string(), z.unknown()),
  ...tier1MetaEnvelope,
}).passthrough();

export const inventorySignalResponseSchema = z.object({
  generatedAtUtc: validDate,
  totalCount: nonNegativeInteger,
  returnedCount: nonNegativeInteger.nullable().optional(),
  totalMatchingCount: nonNegativeInteger.nullable().optional(),
  isTruncated: z.boolean().nullable().optional(),
  snapshotAvailable: z.boolean(),
  snapshotFreshnessUtc: validDate.nullable().optional(),
  snapshotFreshnessStatus: z.string().nullable().optional(),
  warning: z.string().nullable().optional(),
  items: z.array(z.unknown()),
  ...tier1MetaEnvelope,
}).passthrough();

export const inventoryStoreComparisonResponseSchema = z.object({
  generatedAtUtc: validDate,
  stores: z.array(z.object({
    storeId: entityId,
    storeName: z.string(),
    totalSku: nonNegativeInteger,
    totalOnHand: finiteNumber,
    lowStockCount: nonNegativeInteger,
    outOfStockCount: nonNegativeInteger,
    criticalCount: nonNegativeInteger,
    stale90PlusCount: nonNegativeInteger,
    estimatedValue: finiteNumber.nullable(),
    valueCoveragePct: nonNegativePercentage.optional(),
    unknownValueRows: nonNegativeInteger.optional(),
    avgUnitsPerSku: finiteNumber,
    healthySharePct: nonNegativePercentage,
  }).passthrough()),
  sharedRisks: z.array(z.object({
    skuKey: z.string(),
    label: z.string(),
    storeCoverage: nonNegativeInteger,
    impactedStores: z.array(z.string()),
  }).passthrough()),
  summary: z.string(),
  ...tier1MetaEnvelope,
}).passthrough();

export const inventoryActionWorkflowResponseSchema = z.object({
  generatedAtUtc: validDate,
  pendingCount: nonNegativeInteger,
  approvedCount: nonNegativeInteger,
  deferredCount: nonNegativeInteger,
  closedCount: nonNegativeInteger,
  items: z.array(z.unknown()),
  ...tier1MetaEnvelope,
}).passthrough();

export const analyticsActionListResponseSchema = z.object({
  items: z.array(z.unknown()),
  totalCount: nonNegativeInteger,
  page: nonNegativeInteger,
  pageSize: nonNegativeInteger,
  totalPages: nonNegativeInteger,
  ...tier1MetaEnvelope,
}).passthrough();

export const analyticsActionCountsResponseSchema = z.object({
  new: nonNegativeInteger,
  accepted: nonNegativeInteger,
  deferred: nonNegativeInteger,
  rejected: nonNegativeInteger,
  done: nonNegativeInteger,
  ignored: nonNegativeInteger.optional(),
  p1Open: nonNegativeInteger,
  ...tier1MetaEnvelope,
}).passthrough();

export const analyticsActionOutcomeSummaryResponseSchema = z.object({
  meta: analyticsResponseMetaSchema.extend({
    generatedAtUtc: validDate,
    sampleSize: nonNegativeInteger,
    measuredSampleSize: nonNegativeInteger,
  }).passthrough(),
  totals: z.object({
    createdCount: nonNegativeInteger,
    closedCount: nonNegativeInteger,
    openCount: nonNegativeInteger,
  }).passthrough(),
  impact: z.object({
    expectedImpactRsd: nullableNumber.optional(),
    measuredImpactRsd: nullableNumber.optional(),
    measuredImpactSampleCount: nonNegativeInteger,
  }).passthrough(),
}).passthrough();

export const supplierDecisionHubResponseSchema = z.object({
  generatedAtUtc: validDate.optional(),
  periodFromUtc: validDate.nullable().optional(),
  periodToUtc: validDate.nullable().optional(),
  items: z.array(z.unknown()).optional(),
  totalCount: nonNegativeInteger.optional(),
  page: nonNegativeInteger.optional(),
  pageSize: nonNegativeInteger.optional(),
  ...tier1MetaEnvelope,
}).passthrough();

export const decisionPulseResponseSchema = z.object({
  generatedAtUtc: validDate,
  periodFromUtc: validDate.nullable(),
  periodToUtc: validDate.nullable(),
  tenantScope: z.string().min(1),
  suppressedCount: nonNegativeInteger,
  items: z.array(z.unknown()),
  meta: analyticsResponseMetaSchema,
  currentness: z.enum(["current", "latest_known"]).optional(),
  asOfUtc: validDate.nullable().optional(),
}).passthrough();
